import React, { useState } from 'react';
import { Clock, Plus, Trash2, ListMusic, Save, GripVertical } from 'lucide-react';
import { Card, Button } from '../ui';
import { cn } from '../../lib/utils';

interface ServiceItem {
  id: string;
  title: string;
  durationMinutes: number;
  type: 'music' | 'speaking' | 'video' | 'other';
}

export function WorshipServiceRunSheet() {
  const [items, setItems] = useState<ServiceItem[]>([
    { id: '1', title: 'Countdown Video', durationMinutes: 5, type: 'video' },
    { id: '2', title: 'Welcome & Announcements', durationMinutes: 3, type: 'speaking' },
    { id: '3', title: 'Worship Set (3 Songs)', durationMinutes: 15, type: 'music' },
    { id: '4', title: 'Sermon', durationMinutes: 35, type: 'speaking' },
    { id: '5', title: 'Closing Song & Prayer', durationMinutes: 5, type: 'music' }
  ]);

  const [targetDuration, setTargetDuration] = useState<number>(60);
  const [startTime, setStartTime] = useState<string>("10:00");

  const totalMinutes = items.reduce((sum, item) => sum + item.durationMinutes, 0);
  const isOverTime = totalMinutes > targetDuration;

  const addItem = () => {
    const newItem: ServiceItem = {
      id: Math.random().toString(36).substr(2, 9),
      title: 'New Segment',
      durationMinutes: 5,
      type: 'other'
    };
    setItems([...items, newItem]);
  };

  const updateItem = (id: string, field: keyof ServiceItem, value: any) => {
    setItems(items.map(item => item.id === id ? { ...item, [field]: value } : item));
  };

  const removeItem = (id: string) => {
    setItems(items.filter(item => item.id !== id));
  };

  const moveItem = (index: number, direction: -1 | 1) => {
    if (index + direction < 0 || index + direction >= items.length) return;
    const newItems = [...items];
    const temp = newItems[index];
    newItems[index] = newItems[index + direction];
    newItems[index + direction] = temp;
    setItems(newItems);
  };

  // Calculate cumulative times
  const calculateTimes = () => {
    const [hoursStr, minutesStr] = startTime.split(':');
    let currentTotalMinutes = (parseInt(hoursStr) * 60) + parseInt(minutesStr);

    return items.map(item => {
      const h = Math.floor(currentTotalMinutes / 60);
      const m = currentTotalMinutes % 60;
      const timeStr = `${h > 12 ? h - 12 : (h === 0 ? 12 : h)}:${m.toString().padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
      
      currentTotalMinutes += item.durationMinutes;
      
      return { ...item, startTime: timeStr };
    });
  };

  const timedItems = calculateTimes();

  return (
    <div className="max-w-4xl mx-auto p-4 space-y-8 min-h-screen">
      {/* Header */}
      <div className="bg-slate-900 rounded-3xl p-8 md:p-12 text-white shadow-2xl flex items-center justify-between border-b-8 border-purple-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-purple-900/20 blur-3xl"></div>
        <div className="z-10 text-center md:text-left">
          <div className="flex items-center gap-2 text-purple-400 font-bold tracking-widest uppercase mb-4 text-sm justify-center md:justify-start">
            <ListMusic className="w-4 h-4" /> Ministry Tools
          </div>
          <h2 className="text-4xl md:text-5xl font-black mb-2 uppercase tracking-tight">
            Service <span className="text-purple-400">Run-Sheet</span> Timer
          </h2>
          <p className="text-slate-400 max-w-xl text-lg mt-4">
            Plan your worship service down to the minute. Build your timeline, set a target length, and never go over time again.
          </p>
        </div>
      </div>

      {/* Global Settings */}
      <Card className="p-6 bg-white dark:bg-slate-900 border-2 shadow-sm flex flex-col md:flex-row gap-6 items-center justify-between">
        <div className="flex gap-6 w-full md:w-auto">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Service Start Time</label>
            <input 
              type="time" 
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="px-4 py-2 rounded-lg border-2 border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 focus:border-purple-500 outline-none font-bold"
            />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Target Length (Min)</label>
            <input 
              type="number" 
              value={targetDuration}
              onChange={(e) => setTargetDuration(Number(e.target.value))}
              className="w-24 px-4 py-2 rounded-lg border-2 border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 focus:border-purple-500 outline-none font-bold"
            />
          </div>
        </div>
        
        {/* Status indicator */}
        <div className={cn(
          "px-6 py-4 rounded-xl border-2 flex items-center gap-4 w-full md:w-auto transition-colors",
          isOverTime ? "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800" : "bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800"
        )}>
          <Clock className={cn("w-8 h-8", isOverTime ? "text-red-500" : "text-emerald-500")} />
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Actual Length</div>
            <div className={cn("text-2xl font-black", isOverTime ? "text-red-600 dark:text-red-400" : "text-emerald-600 dark:text-emerald-400")}>
              {totalMinutes} Minutes
            </div>
            {isOverTime && <div className="text-xs font-bold text-red-500 mt-1">{(totalMinutes - targetDuration)} minutes over target!</div>}
          </div>
        </div>
      </Card>

      {/* The Run Sheet */}
      <div className="space-y-3">
        <div className="flex justify-between items-end mb-4 px-2">
          <h3 className="font-bold text-slate-700 dark:text-slate-300 text-lg">Timeline Builder</h3>
          <Button onClick={addItem} size="sm" className="bg-purple-600 hover:bg-purple-700 text-white">
            <Plus className="w-4 h-4 mr-2" /> Add Item
          </Button>
        </div>

        {timedItems.map((item, index) => (
          <Card key={item.id} className="p-0 overflow-hidden flex bg-white dark:bg-slate-900 border-2 transition-all hover:border-purple-300 dark:hover:border-purple-700 group">
            
            {/* Start Time Column */}
            <div className="bg-slate-100 dark:bg-slate-950 w-28 shrink-0 flex flex-col items-center justify-center border-r dark:border-slate-800 p-4 relative">
              <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col gap-1">
                <button disabled={index === 0} onClick={() => moveItem(index, -1)} className="p-1 text-slate-400 hover:text-purple-600 disabled:opacity-30"><GripVertical className="w-4 h-4 -rotate-90" /></button>
                <button disabled={index === items.length - 1} onClick={() => moveItem(index, 1)} className="p-1 text-slate-400 hover:text-purple-600 disabled:opacity-30"><GripVertical className="w-4 h-4 rotate-90" /></button>
              </div>
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Starts At</span>
              <span className="font-black text-purple-600 dark:text-purple-400">{item.startTime}</span>
            </div>

            {/* Content Column */}
            <div className="flex-1 p-4 flex flex-col sm:flex-row gap-4 items-center">
              <input
                type="text"
                value={item.title}
                onChange={(e) => updateItem(item.id, 'title', e.target.value)}
                className="flex-1 font-bold text-lg bg-transparent border-b-2 border-transparent hover:border-slate-200 focus:border-purple-500 outline-none px-2 py-1 w-full"
                placeholder="Item Title"
              />
              
              <div className="flex items-center gap-4 w-full sm:w-auto justify-end">
                <select
                  value={item.type}
                  onChange={(e) => updateItem(item.id, 'type', e.target.value)}
                  className="px-3 py-2 rounded-lg text-sm font-bold bg-slate-50 dark:bg-slate-800 border-2 dark:border-slate-700 outline-none focus:border-purple-500"
                >
                  <option value="music">Music</option>
                  <option value="speaking">Speaking</option>
                  <option value="video">Video</option>
                  <option value="other">Other</option>
                </select>

                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={item.durationMinutes}
                    onChange={(e) => updateItem(item.id, 'durationMinutes', Math.max(1, Number(e.target.value)))}
                    className="w-20 px-3 py-2 rounded-lg font-black text-center bg-slate-50 dark:bg-slate-800 border-2 dark:border-slate-700 outline-none focus:border-purple-500"
                  />
                  <span className="text-xs font-bold text-slate-500 uppercase">Min</span>
                </div>

                <button 
                  onClick={() => removeItem(item.id)}
                  className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <div className="flex justify-center pt-8">
        <Button variant="secondary" onClick={() => window.print()} className="border-2 font-bold px-8">
          <Save className="w-4 h-4 mr-2" /> Print Final Run-Sheet
        </Button>
      </div>
    </div>
  );
}
